import getUserInfo from "./user-auth/UserInfo";

/**
 * Configurações de timeout para diferentes tipos de relatório
 */
const TIMEOUT_CONFIG = {
  // Timeout padrão para relatórios simples (2 minutos)
  DEFAULT: 120000,
  // Timeout para relatórios compilados (10 minutos - podem ter muitos projetos)
  COMPILED: 600000,
  // Timeout de conexão inicial (30 segundos)
  CONNECTION: 30000,
};

/**
 * Classe para gerenciar o timeout do WebSocket
 */
class WebSocketTimeoutManager {
  constructor(ws, timeout, onTimeout) {
    this.ws = ws;
    this.timeout = timeout;
    this.onTimeout = onTimeout;
    this.timeoutId = null;
    this.isResolved = false;
  }

  start() {
    this.timeoutId = setTimeout(() => {
      if (!this.isResolved) {
        this.onTimeout();
        this.cleanup();
      }
    }, this.timeout);
  }

  resolve() {
    this.isResolved = true;
    this.cleanup();
  }

  cleanup() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    // Fecha WebSocket se ainda estiver aberto
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close();
    }
  }
}

/**
 * Envia dados para geração de relatório via WebSocket com timeout configurável.
 *
 * @param {string} option - Tipo de relatório
 * @param {string} additionalInfo - Informações adicionais (nome do projeto, gerência, etc)
 * @param {string|Object} referencePeriod - Período de referência
 * @param {boolean} isCompiledReport - Se é um relatório compilado
 * @param {string|null} compiledManagement - Sigla da gerência para relatórios compilados
 * @returns {Promise} Promise que resolve com a resposta do servidor
 */
export const sendReportData = async (
  option,
  additionalInfo,
  referencePeriod,
  isCompiledReport = false,
  compiledManagement = null
) => {
  return new Promise((resolve, reject) => {
    const userInfo = getUserInfo();

    const connectionKey = process.env.REACT_APP_CONNECTION_KEY;

    const userChatEmail = userInfo.email;
    const userChatName = userInfo.fullName;
    const userChatID = userInfo.uid;

    const wsUrl = `${process.env.REACT_APP_WS_BASE_URL}/report/ws/${connectionKey}/${userChatEmail}/${userChatName}/${userChatID}`;

    // Determina timeout baseado no tipo de relatório
    const timeout = isCompiledReport
      ? TIMEOUT_CONFIG.COMPILED
      : TIMEOUT_CONFIG.DEFAULT;


    let ws;
    let timeoutManager;
    let connectionTimeoutId;

    try {
      ws = new WebSocket(wsUrl);
    } catch (error) {
      reject(new Error("Falha ao conectar com o servidor de relatórios"));
      return;
    }

    // Timeout para conexão inicial
    connectionTimeoutId = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.close();
        reject(
          new Error(
            "Tempo limite de conexão excedido. Por favor, tente novamente."
          )
        );
      }
    }, TIMEOUT_CONFIG.CONNECTION);

    // Gerenciador de timeout para a operação completa
    timeoutManager = new WebSocketTimeoutManager(ws, timeout, () => {
      reject(
        new Error(
          isCompiledReport
            ? "O relatório compilado está demorando mais que o esperado. O processamento continua em segundo plano."
            : "Tempo limite excedido para geração do relatório. Por favor, tente novamente."
        )
      );
    });

    ws.onopen = () => {
      // Limpa timeout de conexão
      clearTimeout(connectionTimeoutId);

      // Inicia timeout da operação
      timeoutManager.start();


      const message = {
        user_id: userInfo.uid,
        user_email: userInfo.email,
        user_name: userInfo.fullName,
        reportType: {
          option: option,
          additionalInfo: additionalInfo,
          isCompiledReport: isCompiledReport,
          compiledManagement: compiledManagement,
        },
        referencePeriod:
          option === "specific responsible" ? "ALL" : referencePeriod,
      };
      ws.send(JSON.stringify(message));
    };

    ws.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);

        if (response.error_message) {
          timeoutManager.resolve();
          resolve({ 
            ...response, 
            isErrorResponse: true,
            report_identifier: null 
          });
          return;
        }

        timeoutManager.resolve();
        resolve(response);
      } catch (parseError) {
        timeoutManager.resolve();
        reject(new Error("Erro ao processar resposta do servidor"));
      }
    };

    ws.onerror = (error) => {
      clearTimeout(connectionTimeoutId);
      timeoutManager.resolve();
      reject(
        new Error("Erro de conexão com o servidor. Por favor, tente novamente.")
      );
    };

    ws.onclose = (event) => {
      clearTimeout(connectionTimeoutId);

      if (!event.wasClean && !timeoutManager.isResolved) {
        timeoutManager.resolve();
        reject(new Error("Conexão perdida com o servidor"));
      }
    };
  });
};

/**
 * Versão com retry automático para relatórios compilados
 *
 * @param {string} option - Tipo de relatório
 * @param {string} additionalInfo - Informações adicionais
 * @param {string|Object} referencePeriod - Período de referência
 * @param {boolean} isCompiledReport - Se é um relatório compilado
 * @param {string|null} compiledManagement - Sigla da gerência
 * @param {number} maxRetries - Número máximo de tentativas
 * @returns {Promise} Promise que resolve com a resposta do servidor
 */
export const sendReportDataWithRetry = async (
  option,
  additionalInfo,
  referencePeriod,
  isCompiledReport = false,
  compiledManagement = null,
  maxRetries = 2
) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await sendReportData(
        option,
        additionalInfo,
        referencePeriod,
        isCompiledReport,
        compiledManagement
      );
    } catch (error) {
      lastError = error;

      // Não faz retry se for timeout de relatório compilado (pode estar processando)
      if (
        isCompiledReport &&
        error.message.includes("processamento continua em segundo plano")
      ) {
        throw error;
      }

      // Espera antes de tentar novamente (backoff exponencial)
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  throw lastError;
};
